import { api, type Activity } from './api';
import { useAuth } from './auth';

/**
 * The approve-then-send rule, shared by every surface that can send a draft
 * (the review queue and the activity card on the prospect record page) so the
 * two cannot drift apart.
 *
 * Two server-side facts shape it. `sendActivity` refuses a message without
 * `approved_at`, and saving edits withdraws any approval — so anything that
 * sends has to save first and (re-)approve afterwards, never before. Getting
 * the order wrong would save the edit, drop the approval, and then be refused
 * by the send. And the approve route is admin-only, so a non-admin is never
 * offered that step: they can send only what an admin has already approved.
 */
export function useDraftApproval(
  activity: Pick<Activity, 'id' | 'approved_at' | 'channel'>,
  draft: { subject: string; body: string; dirty: boolean },
) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const approved = Boolean(activity.approved_at);
  const { subject, body, dirty } = draft;

  /** True when the next send or schedule will approve as part of the action. */
  const willApprove = isAdmin && (dirty || !approved);
  /** Non-admins get no send control at all for an unapproved draft. */
  const canSend = isAdmin || approved;

  const commitAndApprove = async () => {
    if (dirty) await api.put(`/api/outreach/${activity.id}`, { subject, body });
    if (willApprove) await api.post(`/api/outreach/${activity.id}/approve`);
  };

  const sendLabel =
    activity.channel === 'email' ? (willApprove ? 'Approve & send now' : 'Send now') : 'Mark sent & copy';
  const scheduleLabel = willApprove ? 'Approve & schedule' : 'Schedule best time';

  return { isAdmin, approved, willApprove, canSend, commitAndApprove, sendLabel, scheduleLabel };
}
